// @flow
import React, { Component } from 'react';
import cx from 'classnames';
import i18n from 'app/common/lib/i18n';
import { Box, Icon, Toast } from 'gestalt';

export type BlockingActionType =
  | 'create_section'
  | 'delete_board'
  | 'delete_pins'
  | 'delete_section'
  | 'merge'
  | 'move_pins';

// TODO(yansun): add color option to spinner so this doesn't need to be a thing
//   remember to remove the index.scss too when removing <WhiteSpinner>
function WhiteSpinner() {
  return (
    <Box display="flex" justifyContent="around" overflow="hidden">
      <div className="boardBlockingActionToastWhiteSpinner">
        <Icon
          accessibilityLabel={i18n._(
            'board action pending',
            'accessibility label for board action toast to indicate pending action',
          )}
          color="white"
          icon="knoop"
          size={40}
        />
      </div>
    </Box>
  );
}

const DEFAULT_DURATION = 2000;

type Props = {|
  actionType: BlockingActionType,
  actionStatus: 'inProgress' | 'completed',
  duration: number,
  location: {
    name: string,
    isSection: boolean,
  },
  onHide: () => void,
|};

type State = {
  visible: boolean,
  hiding: boolean,
};

export default class BoardBlockingActionToast extends Component<Props, State> {
  state: State = {
    hiding: false,
    visible: false,
  };

  componentDidMount() {
    if (!this.firstLine) {
      return;
    }
    window.setTimeout(() => {
      this.setState({ visible: true });
    }, 100); // Wait 100ms to allow rendering engine to paint
    this.timeout = window.setTimeout(this.handleHide, this.props.duration || DEFAULT_DURATION);
  }

  get firstLine(): ?string {
    const {
      actionType,
      actionStatus,
      location: { isSection: locationIsSection },
    } = this.props;
    const isComplete = actionStatus === 'completed';
    switch (actionType) {
      case 'create_section':
        return isComplete
          ? i18n._(
              'Created section',
              'toast text to indicate a section has been created successfully',
            )
          : i18n._('Creating section', 'toast text to indicate a section is being created');
      case 'delete_pins':
        if (locationIsSection) {
          return isComplete
            ? i18n._(
                'Deleted Pins from',
                'toast text to notify Pins have been deleted from a section',
              )
            : i18n._(
                'Deleting Pins from',
                'toast text to notify Pins are being deleted from a section',
              );
        } else {
          return isComplete
            ? i18n._(
                'Deleted Pins from',
                'toast text to notify Pins have been deleted from a board',
              )
            : i18n._(
                'Deleting Pins from',
                'toast text to notify Pins are being deleted from a board',
              );
        }
      case 'move_pins':
        if (locationIsSection) {
          return isComplete
            ? i18n._('Moved to', 'toast text to notify pins have been moved to this section')
            : i18n._('Moving to', 'toast text to notify pins are being moved to this section');
        } else {
          return isComplete
            ? i18n._('Moved to', 'toast text to notify pins have been moved to this board')
            : i18n._('Moving to', 'toast text to notify pins are being moved to this board');
        }
      case 'merge':
        if (locationIsSection) {
          return isComplete
            ? i18n._(
                'Moved to',
                'toast text to notify anotehr section has been merged to this section',
              )
            : i18n._(
                'Moving to',
                'toast text to notify another section is being merged to this section',
              );
        } else {
          return isComplete
            ? i18n._('Moved to', 'toast text to notify another board has been merged to this board')
            : i18n._(
                'Moving to',
                'toast text to notify another board is being merged to this board',
              );
        }
      default:
        return null;
    }
  }

  handleHide = () => {
    const HIDE_DURATION = 200; // Keep in sync with ToastBase
    this.setState({ hiding: true });
    this.timeout = window.setTimeout(this.props.onHide, HIDE_DURATION);
  };

  handleMouseEnter = () => {
    window.clearTimeout(this.timeout);
  };

  handleMouseLeave = () => {
    this.timeout = window.setTimeout(this.handleHide, this.props.duration || DEFAULT_DURATION);
  };

  timeout: ?number;

  render() {
    const { actionStatus, location } = this.props;
    const { visible, hiding } = this.state;
    const firstLine = this.firstLine;
    if (!firstLine) {
      return null;
    }
    const secondLine = actionStatus === 'inProgress' ? `${location.name}...` : location.name;
    return (
      <div
        className={cx('ToastBase', 'react', { visible }, { hiding })}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        <Toast
          color="darkGray"
          text={[firstLine, secondLine]}
          thumbnail={actionStatus === 'completed' ? undefined : <WhiteSpinner />}
        />
      </div>
    );
  }
}
